import { createApi } from '@reduxjs/toolkit/query/react';

import { buildQueryParams } from 'helpers/utils';
import API_ROUTES from 'services/apiRoutes';
import { baseQueryWithAuth } from 'services/baseQueryWithAuth';
import { error as sliceError, start, success } from 'store/transactionsDistinctRecipients/slice';
import { ITransactionsDistinctRecipients } from 'store/transactionsDistinctRecipients/types';
import { isAbortError } from 'helpers/rtk-utils';

export interface TransactionsDistinctRecipientsParams {
  start_date: string;
  end_date: string;
  client_id?: number | null;
}

export const transactionsDistinctRecipientsApi = createApi({
  reducerPath: 'transactionsDistinctRecipientsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['TransactionsDistinctRecipients'],
  endpoints: builder => ({
    getTransactionsDistinctRecipients: builder.query<
      ITransactionsDistinctRecipients,
      TransactionsDistinctRecipientsParams
    >({
      query: ({ start_date, end_date, client_id }) => {
        const params = buildQueryParams({
          start_date,
          end_date,
          client_id,
        });

        return API_ROUTES.GET_TRANSACTIONS_DISTINCT_RECIPIENTS(params);
      },
      // Normalize the response and hydrate the legacy slice so selectors continue to work.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        dispatch(start());
        try {
          const { data } = await queryFulfilled;
          dispatch(success(data));
        } catch (err) {
          type ApiErrorResponse = { status: number | string; data?: unknown; error?: unknown };

          const isApiErrorResponse = (err: unknown): err is ApiErrorResponse =>
            typeof err === 'object' && err !== null && 'status' in err;

          // Ignore intentional aborts from component unmounts or dependency changes
          if (isAbortError(err)) {
            return;
          }

          let message = 'Failed to load recipients';

          if (isApiErrorResponse(err)) {
            if (err.data && typeof err.data === 'object' && 'message' in err.data) {
              message = String((err.data as { message?: unknown }).message ?? message);
            } else if (typeof err.error === 'string') {
              message = err.error;
            }
          } else if (typeof err === 'object' && err !== null && 'message' in err) {
            message = String((err as { message?: unknown }).message ?? message);
          }

          dispatch(sliceError(message));
        }
      },
      transformResponse: (response: any, _meta, arg): ITransactionsDistinctRecipients => {
        const recipients = Array.isArray(response)
          ? response
          : response?.recipients || response?.distinct_recipients || null;

        return {
          client_id: arg.client_id ?? null,
          start_date: arg.start_date ?? null,
          end_date: arg.end_date ?? null,
          recipients,
        };
      },
      providesTags: ['TransactionsDistinctRecipients'],
    }),
  }),
});

export const {
  useGetTransactionsDistinctRecipientsQuery,
  useLazyGetTransactionsDistinctRecipientsQuery,
} = transactionsDistinctRecipientsApi;
